import { useTranslation } from '@shared/core/i18n';
import { useMemo } from 'react';
import { SearchParams } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { formatScheduleItemDate, formatScheduleTimeframe } from '@shared/utils/scheduleFormatting';
import { getTimeZoneAbbr } from './ScheduleList.utils';
import { useEventLocale } from '@shared/core/i18n/LocaleContext';

type RouteBuilder = Readonly<{
  buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => string;
}>;

export type ScheduleListTranslations = Readonly<{
  addToCalendar: string;
  whereToStay: string;
  addToCal: string;
  dressCode: string;
  getDirections: string;
  moreDetails: string;
  timeframeTz: (showTZ: boolean, startDateTime?: number, endDateTime?: number, timezone?: string) => string | undefined;
  timeTz: (time?: string, timezoneAbbr?: string) => string | undefined;
  description: (
    bookYourStay: RouteBuilder,
    description?: string | null,
    dressCode?: string | null,
    eventHandle?: string | null,
    clientUri?: string | null,
    includeAccommodationsLink?: boolean,
  ) => string | undefined;
  toBeAnnounced: string;
  toBeAnnouncedDescription: string;
}>;

export const useTranslations = (): ScheduleListTranslations => {
  const { t, tq } = useTranslation('guestKit');
  const translations = t('common');
  const { language } = useEventLocale();

  const value = useMemo<ScheduleListTranslations>(() => {
    return {
      addToCalendar: translations.addToCalendar(),
      whereToStay: translations.whereToStay(),
      addToCal: translations.addToCal(),
      dressCode: translations.dressCode(),
      getDirections: translations.getDirections(),
      moreDetails: translations.moreDetails(),
      description: (bookYourStay, description, dressCode, eventHandle, clientUri, includeAccommodationsLink = true) => {
        const contentParts: string[] = [];
        if (description) contentParts.push(description);
        if (dressCode) contentParts.push(`${translations.dressCode()}: ${dressCode}`);

        if (!eventHandle || !clientUri) {
          return contentParts.length > 0 ? tq`${contentParts.join('\n\n')}` : undefined;
        }

        const normalizedClientUri = clientUri.endsWith('/') ? clientUri.slice(0, -1) : clientUri;
        const scheduleUrl = new URL(`${normalizedClientUri}/${eventHandle}/schedule`);
        scheduleUrl.searchParams.set('utm_medium', 'calendar');
        scheduleUrl.searchParams.set('utm_campaign', 'schedule_item');
        scheduleUrl.searchParams.set('utm_source', 'schedule_list_item');

        const linkParts: string[] = [`${translations.moreDetails()}:\n${scheduleUrl.toString()}`];

        if (includeAccommodationsLink) {
          const accommodationsUrl =
            normalizedClientUri +
            bookYourStay.buildRelativeUrl(eventHandle, {
              utm_medium: 'calendar',
              utm_campaign: 'schedule_item',
              utm_source: 'schedule_list_item',
            });
          linkParts.push(`${translations.whereToStay()}:\n${accommodationsUrl}`);
        }

        const allParts = [...contentParts, linkParts.join('\n\n')];
        return tq`${allParts.join('\n\n')}`;
      },
      timeframeTz: (showTZ: boolean, startDateTime?: number, endDateTime?: number, timezone?: string): string | undefined => {
        const start = startDateTime ? formatScheduleItemDate(new Date(startDateTime), language, timezone) : undefined;

        if (!start) return undefined;

        const end = endDateTime ? formatScheduleItemDate(new Date(endDateTime), language, timezone) : undefined;

        const timezoneAbbr = getTimeZoneAbbr(startDateTime, timezone);

        // Single time only - return time with or without timezone
        if (!end) {
          return showTZ ? translations.timeTz({ time: start.time, timezoneAbbr }) : start.time;
        }

        // Use shared formatting logic
        // Note: translations.timeframe and translations.timeframeMultiDay refer to the underlying
        // i18n translation functions from t('common'), not methods from ScheduleListTranslations.
        // These are raw TranslateFunction instances that formatScheduleTimeframe expects.
        return formatScheduleTimeframe({
          start,
          end,
          timeframeMultiDay: showTZ ? translations.timeframeMultiDayTz : translations.timeframeMultiDay,
          timeframe: showTZ ? translations.timeframeTz : translations.timeframe,
          language,
          ...(showTZ && { timezoneAbbr }),
        });
      },
      timeTz: (time, timezoneAbbr) => translations.timeTz({ time, timezoneAbbr }),
      toBeAnnounced: translations.toBeAnnouncedHeader(),
      toBeAnnouncedDescription: translations.toBeAnnouncedDescription(),
    };
  }, [tq, translations, language]);

  return value;
};
